import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import './Auth.css';

const RESEND_COOLDOWN_SECONDS = 60;

const ForgotPassword = () => {
  const { forgotPassword, resetPassword } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [step, setStep] = useState('request'); // 'request' | 'reset'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(cooldownRef.current);
  }, [resendCooldown]);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const result = await forgotPassword(email.trim());
    setLoading(false);

    if (result.success) {
      setSuccessMsg(result.message);
      setStep('reset');
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } else {
      setError(result.error);
      if (result.retryAfterSeconds) setResendCooldown(result.retryAfterSeconds);
    }
  };

  const handleResend = async () => {
    setError('');
    setSuccessMsg('');
    const result = await forgotPassword(email.trim());
    if (result.success) {
      setSuccessMsg(result.message);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } else {
      setError(result.error);
      if (result.retryAfterSeconds) setResendCooldown(result.retryAfterSeconds);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError(t('auth.passwordTooShort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setLoading(true);
    const result = await resetPassword(email.trim(), code.trim(), newPassword);
    setLoading(false);

    if (result.success) {
      navigate('/login', { state: { passwordResetSuccess: true } });
    } else {
      setError(result.error);
      setCode('');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>📈 CryptoBot</h1>
          <p>{t('auth.forgotPasswordTitle')}</p>
        </div>

        {error && <div className="auth-error">{error}</div>}
        {successMsg && step === 'reset' && <div className="verify-resend-success">{successMsg}</div>}

        {step === 'request' ? (
          <form onSubmit={handleRequestCode} className="auth-form">
            <p className="verify-email-hint">{t('auth.forgotPasswordHint')}</p>
            <div className="form-group">
              <label htmlFor="email">{t('auth.email')}</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoFocus
              />
            </div>
            <button type="submit" className="auth-button" disabled={loading || !email.trim()}>
              {loading ? t('auth.verifyChecking') : t('auth.forgotPasswordSubmit')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleReset} className="auth-form">
            <p className="verify-email-hint">
              {t('auth.verifySentTo')} <strong>{email}</strong>
            </p>

            <div className="form-group">
              <label htmlFor="reset-code">{t('auth.verifyCodeLabel')}</label>
              <input
                type="text"
                id="reset-code"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                className="verify-code-input"
                autoComplete="one-time-code"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="new-password">{t('settings.newPassword')}</label>
              <input
                type="password"
                id="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirm-new-password">{t('auth.confirmPassword')}</label>
              <input
                type="password"
                id="confirm-new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={loading || code.length !== 6 || !newPassword || !confirmPassword}
            >
              {loading ? t('auth.verifyChecking') : t('auth.forgotPasswordReset')}
            </button>

            <button
              type="button"
              className="verify-resend-btn"
              onClick={handleResend}
              disabled={resendCooldown > 0}
            >
              {resendCooldown > 0
                ? `${t('auth.verifyResendWait')} (${resendCooldown}s)`
                : t('auth.verifyResend')}
            </button>
          </form>
        )}

        <div className="auth-footer">
          <p>
            <Link to="/login">{t('auth.verifyBackToLogin')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
